import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Menu, X, Info, BookOpenCheck } from "lucide-react";
import { BIZ, SERVICES, WHY_CLIENTS, HOW_ENGAGEMENT_WORKS } from "./cpaTaxData";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import AccountingInquiryForm from "@/components/AccountingInquiryForm";

const BLUE = "#1D4ED8";
const NAVY = "#0F2555";
const CREAM = "#F5F7FA";
const INK = "#1E2433";
const SLATE = "#57607A";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

// A ledger-book aesthetic — ruled rows and column-like alignment — fits a bookkeeping firm more
// literally than a card grid.
export default function CpaTaxSite() {
  const [open, setOpen] = useState(false);
  const links = [["Services", "#services"], ["Why Us", "#why"], ["How It Works", "#how"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Inter', sans-serif" }} data-testid="cpatax-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Roboto+Mono:wght@500;600&display=swap'); .ledger-mono{font-family:'Roboto Mono',monospace;}`}</style>

      <div data-testid="accounting-disclaimer" className="bg-black text-white text-center text-xs py-2.5 px-4">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: BLUE }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 shadow-sm" style={{ background: NAVY }}>
        <nav className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#top" data-testid="cpatax-logo" className="flex items-center gap-2 text-white font-bold text-base">
            <BookOpenCheck size={20} style={{ color: "#93C5FD" }} /> CPA Tax &amp; Bookkeeping
          </a>
          <div className="hidden md:flex items-center gap-7">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-white/75 hover:text-white transition-colors">{l}</a>)}
          </div>
          <a href={`tel:${BIZ.phone}`} data-testid="cpatax-header-cta" className="hidden sm:inline-flex rounded-full px-5 py-2 text-sm font-semibold" style={{ background: BLUE, color: "white" }}>Call Now</a>
          <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="cpatax-menu-toggle" aria-label="Menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1" style={{ background: NAVY }}>{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium text-white/85">{l}</a>)}</div>}
      </header>

      {/* HERO — a ledger-entry style opening, "Since 2007" as the header stamp */}
      <section className="py-12 sm:py-16" style={{ background: CREAM }} data-testid="cpatax-hero">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid md:grid-cols-[0.42fr_1fr] shadow-sm">
          <aside className="p-7 sm:p-10 flex flex-col justify-between" style={{ background: NAVY, color: "white" }}>
            <BookOpenCheck size={30} style={{ color: "#93C5FD" }} />
            <div className="mt-12 md:mt-0">
              <p className="ledger-mono text-xs text-white/55">MANDALUYONG CITY</p>
              <p className="ledger-mono text-2xl mt-2">EST. 2007</p>
            </div>
          </aside>
          <div className="bg-white p-7 sm:p-12 lg:p-16 border-y border-r" style={{ borderColor: "#D9E2F1" }}>
            <p className="ledger-mono text-xs mb-6" style={{ color: BLUE }}>PRACTICAL SME ACCOUNTING &amp; TAX SUPPORT</p>
            <motion.h1 {...fadeUp} className="text-3xl sm:text-5xl font-extrabold leading-[1.12]" style={{ color: NAVY }}>{BIZ.tagline}</motion.h1>
            <motion.p {...fadeUp} className="mt-5 max-w-lg" style={{ color: SLATE }}>Bookkeeping, tax compliance and general accounting support for local businesses.</motion.p>
            <motion.div {...fadeUp} className="mt-8 flex flex-wrap gap-4">
              <a href="#contact" data-testid="cpatax-hero-cta" className="px-7 py-3.5 text-sm font-semibold" style={{ background: BLUE, color: "white" }}>Ask About Your Books</a>
              <a href={`tel:${BIZ.phone}`} data-testid="cpatax-hero-call" className="px-7 py-3.5 text-sm font-semibold border" style={{ color: NAVY, borderColor: "#B7C5DC" }}>Call {BIZ.phoneDisplay}</a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES — ledger rows, ruled like a spreadsheet */}
      <section id="services" className="py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-bold text-center mb-10" style={{ color: NAVY }}>Services</motion.h2>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="grid grid-cols-[auto_1fr] gap-x-4 px-6 py-3 ledger-mono text-[10px] uppercase tracking-wide" style={{ background: "#E7EDF7", color: SLATE }}>
              <span>No.</span><span>Service</span>
            </div>
            {SERVICES.map((s, i) => (
              <motion.div key={s.title} {...fadeUp} className="grid grid-cols-[auto_1fr] gap-x-4 px-6 py-4 items-baseline" style={{ borderTop: i > 0 ? "1px solid #E7EDF7" : "none" }}>
                <span className="ledger-mono text-sm font-semibold" style={{ color: BLUE }}>{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <p className="font-semibold text-sm" style={{ color: NAVY }}>{s.title}</p>
                  <p className="mt-0.5 text-xs" style={{ color: SLATE }}>{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="why" className="py-16" style={{ background: "#E7EDF7" }}>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-bold mb-5" style={{ color: NAVY }}>Why Clients Choose Us</motion.h2>
          <motion.p {...fadeUp} className="text-sm" style={{ color: SLATE }}>{WHY_CLIENTS}</motion.p>
        </div>
      </section>

      {/* HOW ENGAGEMENT WORKS — inline ledger-numbered rows, not circles */}
      <section id="how" className="py-16">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-bold text-center mb-10" style={{ color: NAVY }}>How Engagement Works</motion.h2>
          <div className="space-y-5">
            {HOW_ENGAGEMENT_WORKS.map((s) => (
              <motion.div key={s.n} {...fadeUp} className="flex items-start gap-4 bg-white rounded-lg p-5 shadow-sm">
                <span className="ledger-mono text-lg font-bold shrink-0" style={{ color: BLUE }}>{s.n}</span>
                <div>
                  <p className="font-semibold text-sm" style={{ color: NAVY }}>{s.title}</p>
                  <p className="mt-1 text-xs" style={{ color: SLATE }}>{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-16" style={{ background: "#E7EDF7" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl font-bold mb-6" style={{ color: NAVY }}>Office</h2>
            <ul className="space-y-4 text-sm mb-6">
              <li className="flex items-start gap-3"><MapPin size={18} style={{ color: BLUE }} className="mt-0.5 shrink-0" />{BIZ.address}</li>
              <li className="flex items-start gap-3"><Phone size={18} style={{ color: BLUE }} className="mt-0.5 shrink-0" /><a href={`tel:${BIZ.phone}`} data-testid="cpatax-phone-link" className="hover:underline">{BIZ.phoneDisplay}</a></li>
              <li className="flex items-start gap-3"><Mail size={18} style={{ color: BLUE }} className="mt-0.5 shrink-0" /><a href={`mailto:${BIZ.email}`} data-testid="cpatax-email-link" className="hover:underline">{BIZ.email}</a></li>
              <li className="flex items-start gap-3"><Clock size={18} style={{ color: BLUE }} className="mt-0.5 shrink-0" />{BIZ.hours}</li>
            </ul>
            <div className="rounded-lg overflow-hidden shadow-sm min-h-[220px]">
              <iframe title="CPA Tax & Bookkeeping Firm Co. map" src={BIZ.mapEmbed} className="w-full h-full min-h-[220px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <AccountingInquiryForm
              testidPrefix="cpatax"
              businessName={BIZ.name}
              accent={BLUE}
              accentText="#FFFFFF"
              phone={BIZ.phone}
              phoneDisplay={BIZ.phoneDisplay}
              email={BIZ.email}
            />
          </motion.div>
        </div>
      </section>

      <footer className="py-8" style={{ background: NAVY }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/50">
          <p className="font-bold text-white">CPA Tax &amp; Bookkeeping Firm Co.</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm current details before visiting.</p>
        </div>
      </footer>

      <ConceptCTAPill slug="cpa-tax-bookkeeping-mandaluyong-concept" />
    </div>
  );
}
